import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchMe } from '../lib/api.js';
import { landingPath } from '../lib/landing.js';
import { useSession } from '../lib/session.js';
import { t } from '../lib/i18n.js';

/**
 * Local login (ADR-0004).
 *
 * This screen is a DEVELOPMENT AND UAT CONVENIENCE. In production QMS360 does not
 * authenticate anyone: each app is loaded inside Algihaz's DronaHQ container, which
 * hands the app a signed-in identity. The banner below says so, because a login
 * screen that looks production-ready is how one ends up shipped.
 */
export function Login() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      const from = (location.state as { from?: unknown } | null)?.from;
      const me = await fetchMe().catch(() => null);
      navigate(landingPath(me?.capabilities, from));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("Sign in failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center px-4 py-12">
      <div className="rounded-lg border border-border bg-surface p-8 shadow-card">
        <h1 className="m-0 text-xl font-semibold">{t("Sign in to QMS360")}</h1>

        <p className="mt-2 rounded-sm bg-tone-pending-surface px-3 py-2 text-sm text-content-secondary">
          {t("Development and UAT sign-in. In production each application receives its identity from the Algihaz platform container.")}
        </p>

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-sm font-medium">
              {t("Email")}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="rounded-sm border border-border-strong px-3 py-2 text-content-primary"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-sm font-medium">
              {t("Password")}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="rounded-sm border border-border-strong px-3 py-2 text-content-primary"
            />
          </div>

          {error && (
            <p role="alert" className="m-0 rounded-sm bg-tone-critical-surface px-3 py-2 text-sm text-tone-critical">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="rounded-sm bg-ag-purple px-4 py-2.5 font-medium text-content-inverse disabled:opacity-60"
          >
            {busy ? t("Signing in…") : t("Sign in")}
          </button>
        </form>
      </div>
    </main>
  );
}
